import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from './utils/apiUtils';
import {
  Tabs, Tab,
  Container, Typography, Button, Alert, Box, Paper, CircularProgress,
  Grid, List, ListItem, ListItemText, Divider, TextField, Card, CardContent,
  CardHeader, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails,
  Badge, Avatar, Chip, Tooltip, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CategoryIcon from '@mui/icons-material/Category';
import TestIcon from '@mui/icons-material/BugReport';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Visibility';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import UploadIcon from '@mui/icons-material/Upload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { styled } from '@mui/material/styles';

// Styled component for file input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Custom styles for message display
const ConversationItem = styled(ListItem)(({ theme, selected }) => ({
  borderLeft: selected ? `4px solid ${theme.palette.primary.main}` : 'none',
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CustomerMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: theme.spacing(1),
}));

const BusinessMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(1),
}));

const MessageBubble = styled(Paper)(({ theme, isCustomer, isUnread }) => ({
  padding: theme.spacing(1.5),
  maxWidth: '80%',
  borderRadius: 16,
  backgroundColor: isCustomer 
    ? isUnread 
      ? theme.palette.info.light 
      : theme.palette.grey[100] 
    : theme.palette.primary.light,
  color: isCustomer ? theme.palette.text.primary : theme.palette.primary.contrastText,
  border: isUnread ? `2px solid ${theme.palette.info.main}` : 'none',
  boxShadow: isUnread ? `0 0 8px ${theme.palette.info.main}` : 'none',
}));

const StatusIndicator = styled(Box)(({ theme, status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 16,
  backgroundColor: status === 'connected' ? theme.palette.success.light : theme.palette.error.light,
  color: status === 'connected' ? theme.palette.success.contrastText : theme.palette.error.contrastText,
  marginRight: theme.spacing(1),
}));

const FacebookMessageManager = () => {
  // Main tab state
  const [activeTab, setActiveTab] = useState(0);
  // File upload ref
  const fileInputRef = useRef(null);
  
  // Parameter management states
  const [categories, setCategories] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  
  // Parameter dialog states
  const [parameterDialogOpen, setParameterDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [editingParameterId, setEditingParameterId] = useState(null);
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] = useState(false);
  
  // New parameter state
  const [parameterData, setParameterData] = useState({
    name: '',
    description: '',
    isActive: true,
    effectiveDate: new Date().toISOString().split('T')[0], // Today's date
    expirationDate: '',
    contextNotes: '',
    file: null
  });
  
  // Message management states
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null); // Track the selected message within a conversation
  
  // State for unread message counts
  const [unreadCounts, setUnreadCounts] = useState({
    totalUnreadCount: 0,
    conversationsWithUnread: 0,
    lastChecked: null,
    loading: false
  });
  
  // State for loading and errors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // State for AI response
  const [aiResponse, setAiResponse] = useState('');
  const [editedResponse, setEditedResponse] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseThreadId, setResponseThreadId] = useState(null);
  const [responseRunId, setResponseRunId] = useState(null);
  
  // State for prompt details
  const [promptDetails, setPromptDetails] = useState(null);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  
  // Options
  const [markAsRead, setMarkAsRead] = useState(false);
  
  // Connection status
  const [facebookStatus, setFacebookStatus] = useState('unknown');
  const [openaiStatus, setOpenaiStatus] = useState('unknown');
  const [setupComplete, setSetupComplete] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4-turbo-preview');
  
  // Message management states
  const [responseStatus, setResponseStatus] = useState({});
  const [draftResponses, setDraftResponses] = useState({});
  const [manualResponse, setManualResponse] = useState('');
  
  // Assistant ID status
  const [assistantIdStatus, setAssistantIdStatus] = useState({
    loading: false,
    id: null,
    status: null,
    message: null,
    error: null
  });
  
  // Vector store cleanup status
  const [vectorStoreCleanupStatus, setVectorStoreCleanupStatus] = useState({
    isLoading: false,
    result: null,
    error: null
  });
  
  // Status constants for clear visualization
  const MESSAGE_STATUS = {
    UNREAD: 'unread',
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending_approval',
    APPROVED: 'approved',
    SENT: 'sent',
    ARCHIVED: 'archived'
  };
  
  // Check Facebook and OpenAI connection status
  const checkConnections = async () => {
    try {
      // Check Facebook connection - direct check of Facebook token
      try {
        const fbResponse = await apiFetch('/api/integrations/facebook/status');
        
        if (fbResponse.ok) {
          const fbData = await fbResponse.json();
          // Use the status field directly from the response
          setFacebookStatus(fbData.status === 'connected' ? 'connected' : 'not connected');
          console.log('Facebook connection status:', fbData);
          
          // If not connected, show appropriate message
          if (fbData.status !== 'connected') {
            setError('Facebook API is not connected. Click Authenticate to set up the token.');
          }
        } else {
          const errorData = await fbResponse.json().catch(() => ({}));
          console.error('Facebook status error:', errorData);
          setFacebookStatus('not connected');
          setError(`Facebook connection error: ${errorData.error || errorData.message || 'Unknown error'}`);
        }
      } catch (fbErr) {
        console.error('Facebook connection check failed:', fbErr);
        setFacebookStatus('not connected');
        setError(`Facebook connection error: ${fbErr.message}`);
      }
      
      // Check OpenAI connection using the same endpoint as FacebookAITesting
      try {
        const openaiResponse = await apiFetch('/api/integrations/facebook/ai-connection-status');
        
        if (openaiResponse.ok) {
          const openaiData = await openaiResponse.json();
          console.log('OpenAI connection status:', openaiData);
          
          // Use the same logic as FacebookAITesting
          if (openaiData.status && openaiData.status.connectionSuccessful) {
            setOpenaiStatus('connected');
            setSetupComplete(true);
          } else {
            setOpenaiStatus('not connected');
            if (openaiData.status && openaiData.status.error) {
              setError(prev => prev || `OpenAI connection error: ${openaiData.status.error}`);
            }
          }
        } else {
          const errorData = await openaiResponse.json().catch(() => ({}));
          console.error('OpenAI status error:', errorData);
          setOpenaiStatus('not connected');
          setError(prev => prev || `OpenAI connection error: ${errorData.error || 'Unknown error'}`);
        }
      } catch (aiErr) {
        console.error('OpenAI connection check failed:', aiErr);
        setOpenaiStatus('not connected');
        setError(prev => prev || `OpenAI connection error: ${aiErr.message}`);
      }
    } catch (err) {
      console.error('Error checking connections:', err);
      setError('Error checking connection status: ' + err.message);
    }
  };
  
  // Initialize Facebook authentication
  const initializeFacebookAuth = async () => {
    try {
      setLoading(true);
      setError('');
      
      // First check if the token already exists and verify its status
      const checkResponse = await apiFetch('/api/integrations/facebook/auth/check', {
        method: 'GET'
      });
      
      if (checkResponse.ok) {
        const data = await checkResponse.json();
        
        if (data.needsAuth) {
          // If we need auth, attempt to exchange the environment variable token
          // This uses the FACEBOOK_USER_ACCESS_TOKEN from .env just like the legacy downloader
          const authResponse = await apiFetch('/api/integrations/facebook/auth/token', {
            method: 'POST',
            body: JSON.stringify({
              useEnvToken: true  // Signal to use the env token instead of a provided one
            })
          });
          
          if (authResponse.ok) {
            const authData = await authResponse.json();
            setMessage('Facebook authentication successful using environment token. Token expires on ' + 
              new Date(authData.expires_at).toLocaleDateString());
            
            // Immediately update Facebook status to connected
            setFacebookStatus('connected');
            
            // Also refresh full connection status
            checkConnections();
          } else {
            const errorData = await authResponse.json();
            throw new Error(errorData.error || 'Failed to authenticate with Facebook using environment token');
          }
        } else {
          setMessage('Facebook authentication is already valid. Token is set up correctly.');
          checkConnections();
        }
      } else {
        const errorData = await checkResponse.json();
        throw new Error(errorData.error || 'Failed to check Facebook authentication status');
      }
    } catch (err) {
      setError(`Facebook authentication error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Load unread conversations
  const fetchUnreadConversations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiFetch(`/api/integrations/facebook/unread-messages?markAsRead=${markAsRead}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch unread messages');
      }
      
      const data = await response.json();
      setConversations(data.conversations || []);
      
      // Clear selected conversation when refreshing
      setSelectedConversation(null);
      setConversationMessages([]);
      setAiResponse('');
      
      setMessage(`Found ${data.conversations.length} conversations with unread messages`);
    } catch (err) {
      setError(err.message || 'An error occurred while fetching unread messages');
    } finally {
      setLoading(false);
    }
  };
  
  // No content here - removing simple version of handleSelectConversation
  
  // Generate AI response for selected conversation
  const generateAIResponse = async () => {
    if (!selectedConversation) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Use the content of the manual response field as the message to analyze
      // This allows the user to edit the message before generating a response
      const messageToAnalyze = manualResponse.trim();
      
      if (!messageToAnalyze) {
        throw new Error('Please select or enter a message to generate a response for');
      }

      // Get the timestamp of the selected message to filter conversation history
      const selectedMessageTimestamp = selectedMessage ? new Date(selectedMessage.created_time) : new Date();
      
      // Format the conversation history for AI analysis, filtering to include only messages
      // with timestamps earlier than or equal to the selected message's timestamp
      const conversationHistory = conversationMessages
        .filter(msg => new Date(msg.created_time) <= selectedMessageTimestamp)
        .map(msg => ({
          role: msg.from_id === '241800215676857' ? 'assistant' : 'user', // Business messages as assistant, customer as user
          content: msg.message,
          timestamp: msg.created_time
        }));
      
      // Call the AI analyze endpoint
      const aiAnalyzeResponse = await apiFetch('/api/integrations/facebook/ai-analyze', {
        method: 'POST',
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          conversation: conversationHistory,
          model: selectedModel,
          messageToAnalyze: messageToAnalyze // Send the edited message content for analysis
        })
      });
      
      if (!aiAnalyzeResponse.ok) {
        const errorData = await aiAnalyzeResponse.json();
        throw new Error(errorData.error || 'Failed to generate AI response');
      }
      
      const responseData = await aiAnalyzeResponse.json();
      
      // Update response state
      const aiResponse = responseData.response;
      
      // Create a new draft response or update existing one
      setDraftResponses(prev => ({
        ...prev,
        [selectedConversation.id]: aiResponse
      }));
      
      // Update state with the response
      setAiResponse(aiResponse);
      setEditedResponse(aiResponse);
      
      // Store metadata for later use when sending to Facebook
      setResponseThreadId(responseData.threadId || null);
      setResponseRunId(responseData.runId || null);
      
      // Store prompt details if available
      if (responseData.promptDetails) {
        console.log('=========== PROMPT DETAILS DEBUG ===========');
        console.log('Prompt details received:', responseData.promptDetails);
        console.log('Base instructions:', responseData.promptDetails.baseAssistantInstructions ? 'Present' : 'Missing');
        console.log('Dynamic prompt:', responseData.promptDetails.completeSystemPrompt ? 'Present' : 'Missing');
        
        // Detailed file debugging
        console.log('Has attachedFiles property:', 'attachedFiles' in responseData.promptDetails);
        console.log('attachedFiles is array:', Array.isArray(responseData.promptDetails.attachedFiles));
        console.log('attachedFiles defined:', !!responseData.promptDetails.attachedFiles);
        
        if (responseData.promptDetails.attachedFiles) {
          console.log('Files count:', responseData.promptDetails.attachedFiles.length);
          if (responseData.promptDetails.attachedFiles.length > 0) {
            console.log('First file details:', JSON.stringify(responseData.promptDetails.attachedFiles[0], null, 2));
            console.log('All files have parameters:', responseData.promptDetails.attachedFiles.every(f => !!f.parameter));
          } else {
            console.log('No files in attachedFiles array');
          }
          
          if (responseData.promptDetails.fileStatistics) {
            console.log('File statistics:', responseData.promptDetails.fileStatistics);
          }
        } else {
          console.log('No attachedFiles property or it is null/undefined');
        }
        console.log('==========================================');
        
        setPromptDetails(responseData.promptDetails);
      }
      
      // Update status to AI draft
      setResponseStatus(prev => ({
        ...prev,
        [selectedConversation.id]: MESSAGE_STATUS.AI_DRAFT
      }));
      
      setMessage('AI response generated successfully');
    } catch (err) {
      console.error('Error generating AI response:', err);
      setError(`AI response generation failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Approve and send the AI response
  const approveAndSendResponse = async (edited = false) => {
    if (!selectedConversation) return;
    
    try {
      setLoading(true);
      setError('');
      
      const responseToSend = edited ? editedResponse : aiResponse;
      
      // Send directly to the conversation-message endpoint
      const response = await apiFetch('/api/integrations/facebook/conversation-message', {
        method: 'POST',
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          message: responseToSend
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send response');
      }
      
      // Update status
      setResponseStatus(prev => ({
        ...prev,
        [selectedConversation.id]: MESSAGE_STATUS.SENT
      }));
      
      setMessage('AI response approved and sent to Facebook successfully');
      
      // Clear AI response state
      setAiResponse('');
      setResponseThreadId(null);
      setResponseRunId(null);
      
      // Refresh conversations after a delay
      setTimeout(() => {
        fetchUnreadConversations();
      }, 1000);
      
    } catch (err) {
      setError(err.message || 'An error occurred while sending the response');
    } finally {
      setLoading(false);
    }
  };
  
  // Edit the AI response
  const handleEditResponse = () => {
    setIsEditing(true);
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditedResponse(aiResponse);
    setIsEditing(false);
  };
  
  // Save edited response
  const handleSaveEdit = () => {
    setAiResponse(editedResponse);
    setIsEditing(false);
  };
  
  // Toggle Mark as Read option
  const toggleMarkAsRead = (event) => {
    setMarkAsRead(event.target.checked);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Function to fetch unread message count
  const fetchUnreadCount = async () => {
    try {
      setUnreadCounts(prev => ({ ...prev, loading: true }));
      
      const response = await apiFetch('/api/integrations/facebook/unread-count');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch unread message count');
      }
      
      const data = await response.json();
      setUnreadCounts({
        totalUnreadCount: data.totalUnreadCount || 0,
        conversationsWithUnread: data.conversationsWithUnread || 0,
        lastChecked: data.lastChecked ? new Date(data.lastChecked) : new Date(),
        loading: false
      });
    } catch (err) {
      console.error('Error fetching unread count:', err);
      setUnreadCounts(prev => ({ 
        ...prev, 
        loading: false,
        error: err.message
      }));
    }
  };

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Load parameters when switching to Parameters tab
    if (newValue === 1) {
      loadCategories();
      loadParameters();
    }
  };
  
  // Clean up vector store by removing duplicate files
  const cleanupVectorStore = async () => {
    try {
      setVectorStoreCleanupStatus({
        isLoading: true,
        result: null,
        error: null
      });
      
      const response = await apiFetch('/api/integrations/facebook/cleanup-vector-store', {
        method: 'POST'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to clean up vector store");
      }
      
      const result = await response.json();
      setVectorStoreCleanupStatus({
        isLoading: false,
        result: result,
        error: null
      });
      
      setMessage(`Vector store cleaned up successfully. Now contains ${result.fileCount} unique files.`);
      
      // Refresh prompt details to show updated file list
      if (promptDetails && promptDetails.assistantId) {
        checkAssistantId();
      }
    } catch (err) {
      console.error("Error cleaning up vector store:", err);
      setVectorStoreCleanupStatus({
        isLoading: false,
        result: null,
        error: err.message
      });
      setError(err.message || "An error occurred cleaning up the vector store");
    }
  };
  
  // Function to check assistant ID
  const checkAssistantId = async () => {
    try {
      setAssistantIdStatus(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await apiFetch('/api/integrations/facebook/assistant-id');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to retrieve assistant ID');
      }
      
      const data = await response.json();
      
      if (data.assistantId) {
        setAssistantIdStatus({
          loading: false,
          id: data.assistantId,
          status: 'success',
          message: 'Assistant ID retrieved successfully',
          error: null
        });
        
        // Also update promptDetails if it exists
        if (promptDetails) {
          setPromptDetails({
            ...promptDetails,
            assistantId: data.assistantId
          });
        }
        
        setMessage('Assistant ID retrieved successfully');
      } else {
        setAssistantIdStatus({
          loading: false,
          id: null,
          status: 'warning',
          message: data.message || 'No assistant ID available',
          error: null
        });
      }
    } catch (err) {
      console.error('Error checking assistant ID:', err);
      setAssistantIdStatus({
        loading: false,
        id: null,
        status: 'error',
        message: null,
        error: err.message
      });
      setError(`Failed to retrieve assistant ID: ${err.message}`);
    }
  };
  
  // Function to force initialize a new assistant
  const initializeAssistant = async () => {
    try {
      setAssistantIdStatus(prev => ({ ...prev, loading: true, error: null }));
      setMessage('Initializing OpenAI assistant...');
      
      const response = await apiFetch('/api/integrations/facebook/initialize-assistant', {
        method: 'POST',
        body: JSON.stringify({}) // Empty body as no parameters needed
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initialize assistant');
      }
      
      const data = await response.json();
      
      if (data.success && data.assistantId) {
        setAssistantIdStatus({
          loading: false,
          id: data.assistantId,
          status: 'success',
          message: `Assistant successfully initialized with ID: ${data.assistantId.substring(0, 10)}...`,
          error: null
        });
        
        // Also update promptDetails if it exists
        if (promptDetails) {
          setPromptDetails({
            ...promptDetails,
            assistantId: data.assistantId
          });
        }
        
        setMessage(`OpenAI assistant initialized successfully with ${data.filesAttached} files attached`);
      } else {
        setAssistantIdStatus({
          loading: false,
          id: null,
          status: 'error',
          message: 'Failed to initialize assistant',
          error: data.message || 'Unknown error'
        });
        setError('Failed to initialize OpenAI assistant: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error initializing assistant:', err);
      setAssistantIdStatus({
        loading: false,
        id: null,
        status: 'error',
        message: null,
        error: err.message
      });
      setError(`Failed to initialize OpenAI assistant: ${err.message}`);
    }
  };
  
  // Check connection status, fetch unread count, and check assistant ID on component mount
  useEffect(() => {
    checkConnections();
    if (facebookStatus === 'connected') {
      fetchUnreadCount();
    }
    
    // Always check for assistant ID on component mount
    if (openaiStatus === 'connected') {
      checkAssistantId();
    }
  }, [facebookStatus, openaiStatus]);
  
  // Load parameter categories
  const loadCategories = async () => {
    try {
      setError("");
      const response = await apiFetch('/api/integrations/facebook/ai-categories');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load categories");
      }
      
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };
  
  // Load parameters (with versioning)
  const loadParameters = async () => {
    try {
      setError("");
      const response = await apiFetch('/api/integrations/facebook/ai-parameters');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load parameters");
      }
      
      const data = await response.json();
      
      // Transform backend data to match frontend data structure
      const transformedParameters = (data.parameters || []).map(param => ({
        id: param.id,
        categoryId: param.category_id,
        category: param.category_name,
        name: param.name,
        description: param.description || '',
        isActive: param.is_active === 1,
        effectiveDate: param.effective_date,
        expirationDate: param.expiration_date || null,
        contextNotes: param.context_notes || '',
        hasSpreadsheet: param.has_file === 1,
        filename: param.original_filename || null,
        createdAt: param.created_at
      }));
      
      setParameters(transformedParameters);
    } catch (err) {
      console.error("Error loading parameters:", err);
    }
  };
  
  // Open dialog to add new category
  const openAddCategoryDialog = () => {
    setNewCategory({ name: '', description: '' });
    setCategoryDialogOpen(true);
  };
  
  // Add a new category
  const addCategory = async () => {
    if (!newCategory.name) {
      setError("Category name is required");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      const response = await apiFetch('/api/integrations/facebook/ai-create-categories', {
        method: 'POST',
        body: JSON.stringify({ 
          categories: [{ 
            name: newCategory.name.toUpperCase(), 
            description: newCategory.description || ""
          }] 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create category");
      }
      
      // Reload categories from server
      await loadCategories();
      
      setNewCategory({ name: '', description: '' });
      setCategoryDialogOpen(false);
      setMessage("Category added successfully");
    } catch (err) {
      setError(err.message || "An error occurred creating the category");
    } finally {
      setLoading(false);
    }
  };
  
  // Open parameter dialog
  const openParameterDialog = (categoryId, parameterId = null) => {
    setSelectedCategoryId(categoryId);
    setEditingParameterId(parameterId);
    
    if (parameterId) {
      // Editing existing parameter
      const parameter = parameters.find(p => p.id === parameterId);
      if (parameter) {
        setParameterData({
          name: parameter.name,
          description: parameter.description || '',
          isActive: parameter.isActive,
          effectiveDate: parameter.effectiveDate,
          expirationDate: parameter.expirationDate || '',
          contextNotes: parameter.contextNotes || '',
          file: null
        });
      }
    } else {
      // Creating new parameter
      setParameterData({
        name: '',
        description: '',
        isActive: true,
        effectiveDate: new Date().toISOString().split('T')[0], // Today's date
        expirationDate: '',
        contextNotes: '',
        file: null
      });
    }
    
    setParameterDialogOpen(true);
  };
  
  // Handle parameter file selection
  const handleParameterFileSelect = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Validate file extension
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!['.xlsx', '.xls', '.pdf', '.csv'].includes(fileExtension)) {
        setError(`Invalid file extension. Only Excel (.xlsx, .xls) , PDF (.pdf) and CSV (.csv) files are allowed.`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      setParameterData({
        ...parameterData,
        file: file
      });
    }
  };
  
  // Save parameter
  const saveParameter = async () => {
    if (!parameterData.name || !parameterData.effectiveDate) {
      setError("Parameter name and effective date are required");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      // Create endpoint URL
      const url = editingParameterId 
        ? `/api/integrations/facebook/ai-parameters/${editingParameterId}`
        : '/api/integrations/facebook/ai-parameters';
      
      // Special case: If we have a file, handle it differently
      if (parameterData.file) {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('categoryId', selectedCategoryId);
        formData.append('name', parameterData.name);
        formData.append('effectiveDate', parameterData.effectiveDate);
        formData.append('isActive', parameterData.isActive ? 1 : 0);
        
        if (parameterData.description) {
          formData.append('description', parameterData.description);
        }
        
        if (parameterData.expirationDate) {
          formData.append('expirationDate', parameterData.expirationDate);
        }
        
        if (parameterData.contextNotes) {
          formData.append('contextNotes', parameterData.contextNotes);
        }
        
        formData.append('file', parameterData.file);
        
        // Get token for auth
        const token = localStorage.getItem('token') || 'dummy-token';
        
        // Use native fetch with proper headers
        const response = await fetch(url, {
          method: editingParameterId ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
      } else {
        // Use simple JSON for non-file parameters
        const paramData = {
          categoryId: selectedCategoryId,
          name: parameterData.name,
          effectiveDate: parameterData.effectiveDate,
          isActive: parameterData.isActive ? 1 : 0
        };
        
        if (parameterData.description) {
          paramData.description = parameterData.description;
        }
        
        if (parameterData.expirationDate) {
          paramData.expirationDate = parameterData.expirationDate;
        }
        
        if (parameterData.contextNotes) {
          paramData.contextNotes = parameterData.contextNotes;
        }
        
        const response = await apiFetch(url, {
          method: editingParameterId ? 'PUT' : 'POST',
          body: JSON.stringify(paramData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to save parameter");
        }
      }
      
      // Reload parameters from server
      await loadParameters();
      
      setMessage(editingParameterId ? "Parameter updated successfully" : "Parameter created successfully");
      
      // Reset form and close dialog
      setParameterData({
        name: '',
        description: '',
        isActive: true,
        effectiveDate: new Date().toISOString().split('T')[0],
        expirationDate: '',
        contextNotes: '',
        file: null
      });
      setParameterDialogOpen(false);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.message || "An error occurred saving the parameter");
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle parameter status
  const toggleParameterStatus = async (parameterId) => {
    try {
      setLoading(true);
      setError("");
      
      // Get current parameter
      const parameter = parameters.find(p => p.id === parameterId);
      if (!parameter) {
        throw new Error("Parameter not found");
      }
      
      // Create data object
      const paramData = {
        categoryId: parameter.categoryId,
        name: parameter.name,
        effectiveDate: parameter.effectiveDate,
        isActive: !parameter.isActive ? 1 : 0
      };
      
      // Add optional fields if present
      if (parameter.description) {
        paramData.description = parameter.description;
      }
      
      if (parameter.expirationDate) {
        paramData.expirationDate = parameter.expirationDate;
      }
      
      if (parameter.contextNotes) {
        paramData.contextNotes = parameter.contextNotes;
      }
      
      const response = await apiFetch(`/api/integrations/facebook/ai-parameters/${parameterId}`, {
        method: 'PUT',
        body: JSON.stringify(paramData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update parameter status");
      }
      
      // Reload parameters from server
      await loadParameters();
      
      setMessage(`Parameter ${!parameter.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      setError(err.message || "An error occurred updating parameter status");
    } finally {
      setLoading(false);
    }
  };
  
  // Delete parameter
  const deleteParameter = async (parameterId) => {
    try {
      setLoading(true);
      setError("");
      
      const response = await apiFetch(`/api/integrations/facebook/ai-parameters/${parameterId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete parameter");
      }
      
      // Reload parameters from server
      await loadParameters();
      
      setMessage("Parameter deleted successfully");
    } catch (err) {
      setError(err.message || "An error occurred deleting the parameter");
    } finally {
      setLoading(false);
    }
  };
  
  // Open file preview dialog
  const openFilePreviewDialog = async (parameter) => {
    try {
      setLoading(true);
      setSelectedFile(parameter);
      
      const response = await apiFetch(`/api/integrations/facebook/ai-parameters/${parameter.id}/preview`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load file preview");
      }
      
      const result = await response.json();
      setFilePreview(result.preview || {});
      
      setFilePreviewDialogOpen(true);
    } catch (err) {
      setError(err.message || "An error occurred loading the file preview");
    } finally {
      setLoading(false);
    }
  };
  
  // No content here - removing duplicate state declarations
  
  // Function to save a draft response
  const saveDraftResponse = (conversationId, response) => {
    setDraftResponses(prev => ({
      ...prev,
      [conversationId]: response
    }));
    
    setResponseStatus(prev => ({
      ...prev,
      [conversationId]: MESSAGE_STATUS.DRAFT
    }));
    
    setMessage(`Draft response saved for conversation ${conversationId}`);
  };
  
  // Function to archive a conversation
  const archiveConversation = (conversationId) => {
    setResponseStatus(prev => ({
      ...prev,
      [conversationId]: MESSAGE_STATUS.ARCHIVED
    }));
    
    setMessage(`Conversation ${conversationId} archived`);
  };
  
  // Function to send a manual response
  const sendManualResponse = async () => {
    if (!selectedConversation || !manualResponse.trim()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiFetch('/api/integrations/facebook/conversation-message', {
        method: 'POST',
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          message: manualResponse
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send manual response');
      }
      
      // Update status
      setResponseStatus(prev => ({
        ...prev,
        [selectedConversation.id]: MESSAGE_STATUS.SENT
      }));
      
      setMessage('Manual response sent to Facebook successfully');
      setManualResponse(''); // Clear the input
    } catch (err) {
      setError(err.message || 'An error occurred while sending manual response');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to get status chip color based on message status
  const getStatusColor = (status) => {
    switch (status) {
      case MESSAGE_STATUS.UNREAD:
        return 'error';
      case MESSAGE_STATUS.DRAFT:
        return 'warning';
      case MESSAGE_STATUS.PENDING_APPROVAL:
        return 'info';
      case MESSAGE_STATUS.APPROVED:
        return 'success';
      case MESSAGE_STATUS.SENT:
        return 'success';
      case MESSAGE_STATUS.ARCHIVED:
        return 'default';
      default:
        return 'default';
    }
  };
  
  // Function to get status text based on message status
  const getStatusText = (status) => {
    switch (status) {
      case MESSAGE_STATUS.UNREAD:
        return 'Unread';
      case MESSAGE_STATUS.DRAFT:
        return 'Draft Response';
      case MESSAGE_STATUS.PENDING_APPROVAL:
        return 'Awaiting Approval';
      case MESSAGE_STATUS.APPROVED:
        return 'Approved';
      case MESSAGE_STATUS.SENT:
        return 'Sent';
      case MESSAGE_STATUS.ARCHIVED:
        return 'Archived';
      default:
        return 'Unknown';
    }
  };
  
  // Handle selecting a message from the conversation
  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    setManualResponse(message.message);
    
    // Track message read time by calling the new endpoint
    if (message && message.message_id) {
      try {
        // Don't need to await this or handle the response directly - it's a background tracking operation
        apiFetch('/api/integrations/facebook/message-read', {
          method: 'POST',
          body: JSON.stringify({
            messageId: message.message_id
          })
        }).catch(err => {
          // Log error but don't show to user since this is a background operation
          console.error('Error updating message read time:', err);
        });
      } catch (err) {
        console.error('Error tracking message read time:', err);
        // Don't show error to user for this background operation
      }
    }
  };
  
  // Override handleSelectConversation to include draft and status management
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation.conversation);
    
    // Sort messages in descending chronological order (newest first)
    const sortedMessages = [...conversation.messages].sort((a, b) => 
      new Date(b.created_time) - new Date(a.created_time)
    );
    
    setConversationMessages(sortedMessages);
    
    // Set initial status if not already set
    if (!responseStatus[conversation.conversation.id]) {
      setResponseStatus(prev => ({
        ...prev,
        [conversation.conversation.id]: MESSAGE_STATUS.UNREAD
      }));
    }
    
    // Select the most recent customer message by default
    const customerMessages = sortedMessages.filter(
      msg => msg.from_id !== process.env.REACT_APP_FACEBOOK_PAGE_ID
    );
    
    if (customerMessages.length > 0) {
      const mostRecentCustomerMessage = customerMessages[0];
      setSelectedMessage(mostRecentCustomerMessage);
      
      // Set manual response to the selected message or existing draft if available
      const existingDraft = draftResponses[conversation.conversation.id] || '';
      setManualResponse(existingDraft || mostRecentCustomerMessage.message);
    } else {
      setSelectedMessage(null);
      
      // Set manual response to existing draft if available
      const existingDraft = draftResponses[conversation.conversation.id] || '';
      setManualResponse(existingDraft);
    }
    
    // Clear AI response state
    setAiResponse('');
    setResponseThreadId(null);
    setResponseRunId(null);
  };
  
  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>Facebook Message Manager</Typography>
      <Typography variant="body1" paragraph>
        Manage Facebook messages using OpenAI's Assistants API to generate responses.
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      
      {/* Tabs for main sections */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="facebook message manager tabs">
          <Tab label="Messages" icon={<ChatIcon />} iconPosition="start" />
          <Tab label="Parameters" icon={<CategoryIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {/* Connection Status and Message Count Indicators */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StatusIndicator status={facebookStatus}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
              Facebook:
            </Typography>
            <Typography variant="body2">
              {facebookStatus === 'connected' ? 'Connected' : 'Not Connected'}
            </Typography>
          </StatusIndicator>
          
          {facebookStatus !== 'connected' && (
            <Button
              variant="outlined"
              size="small"
              onClick={initializeFacebookAuth}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Authenticate
            </Button>
          )}
          
          <Button
            variant="outlined"
            size="small"
            onClick={checkConnections}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            Refresh
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <StatusIndicator status={openaiStatus}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
              ChatGPT:
            </Typography>
            <Typography variant="body2">
              {openaiStatus === 'connected' ? 'Connected' : 'Not Connected'}
            </Typography>
          </StatusIndicator>
          
          {openaiStatus === 'connected' && (
            <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="chatgpt-model-select-label">Model</InputLabel>
              <Select
                labelId="chatgpt-model-select-label"
                id="chatgpt-model-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                label="Model"
              >
                <MenuItem value="gpt-4-turbo-preview">GPT-4 Turbo</MenuItem>
                <MenuItem value="gpt-4">GPT-4</MenuItem>
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
        
        {/* Unread Message Count Display */}
        {facebookStatus === 'connected' && (
          <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2">
                {unreadCounts.totalUnreadCount > 0 ? (
                  <>
                    <Badge 
                      badgeContent={unreadCounts.totalUnreadCount} 
                      color="error"
                      sx={{ mr: 1 }}
                    >
                      <MarkEmailUnreadIcon color="primary" />
                    </Badge>
                    {unreadCounts.totalUnreadCount} unread {unreadCounts.totalUnreadCount === 1 ? 'message' : 'messages'} in {unreadCounts.conversationsWithUnread} {unreadCounts.conversationsWithUnread === 1 ? 'conversation' : 'conversations'}
                  </>
                ) : (
                  'No unread messages'
                )}
              </Typography>
              {unreadCounts.lastChecked && (
                <Typography variant="caption" color="text.secondary">
                  Last checked: {unreadCounts.lastChecked.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={markAsRead}
                    onChange={toggleMarkAsRead}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {markAsRead ? 
                      <MarkEmailReadIcon fontSize="small" sx={{ mr: 0.5 }} /> : 
                      <MarkEmailUnreadIcon fontSize="small" sx={{ mr: 0.5 }} />
                    }
                    <Typography variant="caption">Mark as Read</Typography>
                  </Box>
                }
              />
              <Button
                size="small"
                startIcon={unreadCounts.loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={fetchUnreadCount}
                disabled={unreadCounts.loading || facebookStatus !== 'connected'}
              >
                Refresh Count
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
      
      {/* Messages Tab Content */}
      {activeTab === 0 && (
        /* Main Content - Three Column Layout */
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Facebook Message Manager</Typography>
          
          <Button
            variant="contained"
            color="primary"
            onClick={fetchUnreadConversations}
            disabled={loading || facebookStatus !== 'connected'}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          >
            {loading ? "Loading..." : "Load Unread Messages"}
          </Button>
        </Box>
        
        <Typography variant="body2" paragraph>
          {markAsRead ? 
            "Messages will be marked as read on Facebook when fetched." : 
            "Messages will remain unread on Facebook when fetched (test mode)."}
        </Typography>
        
        <Grid container spacing={2}>
          {/* Column 1: Conversation List */}
          <Grid item xs={12} md={2}>
            <Paper 
              variant="outlined" 
              sx={{ 
                height: '700px', 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight="bold">Conversations</Typography>
              </Box>
              
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {conversations.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No conversations found. Click "Load Unread Messages" to fetch messages.
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {conversations.map((convo) => {
                      const convoStatus = responseStatus[convo.conversation.id] || MESSAGE_STATUS.UNREAD;
                      const statusColor = getStatusColor(convoStatus);
                      
                      return (
                        <React.Fragment key={convo.conversation.id}>
                          <ConversationItem
                            button
                            selected={selectedConversation?.id === convo.conversation.id}
                            onClick={() => handleSelectConversation(convo)}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Badge 
                                    badgeContent={convo.conversation.unread_count} 
                                    color="error"
                                    sx={{ mr: 1 }}
                                  >
                                    <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
                                      <ChatIcon fontSize="small" />
                                    </Avatar>
                                  </Badge>
                                  <Typography variant="body2" noWrap>
                                    {convo.conversation.participants?.data?.[0]?.name || 'Facebook User'}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip 
                                    label={getStatusText(convoStatus)} 
                                    color={statusColor} 
                                    size="small" 
                                    sx={{ height: 20, fontSize: '0.7rem', mr: 0.5 }}
                                  />
                                  <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                    {formatDate(convo.conversation.updated_time)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ConversationItem>
                          <Divider />
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* Column 2: Message Thread Display */}
          <Grid item xs={12} md={4}>
            <Paper 
              variant="outlined" 
              sx={{ 
                height: '700px', 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden' 
              }}
            >
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedConversation ? 
                    `Conversation with ${selectedConversation.participants?.data?.[0]?.name || 'Facebook User'}` :
                    'Message Thread'
                  }
                </Typography>
                
                {selectedConversation && (
                  <Chip 
                    label={getStatusText(responseStatus[selectedConversation.id] || MESSAGE_STATUS.UNREAD)} 
                    color={getStatusColor(responseStatus[selectedConversation.id] || MESSAGE_STATUS.UNREAD)} 
                    size="small" 
                  />
                )}
              </Box>
              
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                {!selectedConversation ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Select a conversation to view messages
                    </Typography>
                  </Box>
                ) : conversationMessages.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No messages found in this conversation
                    </Typography>
                  </Box>
                ) : (
                  // Display messages in DESC order (newest first)
                  conversationMessages.map((message, index) => {
                    const isCustomer = message.from_id !== process.env.REACT_APP_FACEBOOK_PAGE_ID; // Business Page ID
                    
                    // Determine if this is the last unread message from customer
                    // We want to highlight the last message from the customer that we need to respond to
                    let isLastUnreadMessage = false;
                    
                    if (isCustomer) {
                      // Check if this is the most recent message from customer
                      // Since messages are sorted in descending order (newest first),
                      // the first customer message is the one we need to highlight
                      const customerMessages = conversationMessages.filter(
                        msg => msg.from_id !== process.env.REACT_APP_FACEBOOK_PAGE_ID
                      );
                      isLastUnreadMessage = customerMessages[0]?.message_id === message.message_id;
                    }
                    
                    // Check if this message is the selected message
                    const isSelected = selectedMessage && selectedMessage.message_id === message.message_id;
                    
                    return isCustomer ? (
                      <CustomerMessage key={message.message_id}>
                        <MessageBubble 
                          isCustomer 
                          isUnread={isLastUnreadMessage}
                          variant="outlined"
                          sx={{ 
                            cursor: 'pointer',
                            border: isSelected ? '2px solid #1976d2' : undefined,
                            boxShadow: isSelected ? '0 0 8px rgba(25, 118, 210, 0.5)' : undefined,
                          }}
                          onClick={() => handleSelectMessage(message)}
                        >
                          <Typography variant="caption" display="block" color="text.secondary">
                            {message.from_name} - {formatDate(message.created_time)}
                                            {isLastUnreadMessage && (
                                              <Chip 
                                                label="Needs Response" 
                                                color="info" 
                                                size="small" 
                                                sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                                              />
                                            )}
                                            {isSelected && (
                                              <Chip 
                                                label="Selected" 
                                                color="primary" 
                                                size="small" 
                                                sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                                              />
                                            )}
                                          </Typography>
                                          <Typography variant="body2">{message.message}</Typography>
                                          
                                          {/* Display AI processing details if available */}
                                          {message.ai_processing_StartTime && (
                                            <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                              <Typography variant="caption" display="block">
                                                {message.ai_processing_StartTime && (
                                                  <>AI processing started: {formatDate(message.ai_processing_StartTime)}</>
                                                )}
                                              </Typography>
                                              {message.ai_processing_StopTime && (
                                                <Typography variant="caption" display="block">
                                                  AI processing completed: {formatDate(message.ai_processing_StopTime)}
                                                </Typography>
                                              )}
                                              {message.assistant_name && (
                                                <Typography variant="caption" display="block">
                                                  Assistant: {message.assistant_name}
                                                </Typography>
                                              )}
                                              {message.message_response_source === 'AI' && (
                                                <Chip 
                                                  label="AI Generated"
                                                  color="info"
                                                  size="small"
                                                  sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }}
                                                />
                                              )}
                                            </Box>
                                          )}
                        </MessageBubble>
                      </CustomerMessage>
                    ) : (
                      <BusinessMessage key={message.message_id}>
                        <MessageBubble 
                          variant="outlined"
                          sx={{ 
                            cursor: 'pointer',
                            border: isSelected ? '2px solid #1976d2' : undefined,
                            boxShadow: isSelected ? '0 0 8px rgba(25, 118, 210, 0.5)' : undefined,
                          }}
                          onClick={() => handleSelectMessage(message)}
                        >
                          <Typography variant="caption" display="block" color="text.secondary">
                            Your Business - {formatDate(message.created_time)}
                            {isSelected && (
                              <Chip 
                                label="Selected" 
                                color="primary" 
                                size="small" 
                                sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2">{message.message}</Typography>
                        </MessageBubble>
                      </BusinessMessage>
                    );
                  })
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* Column 3: Response Area */}
          <Grid item xs={12} md={6}>
            <Paper 
              variant="outlined" 
              sx={{ 
                height: '700px', 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden' 
              }}
            >
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight="bold">Response</Typography>
              </Box>
              
              <Box sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                p: 2,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                {!selectedConversation ? (
                  <Box sx={{ p: 2, textAlign: 'center', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Select a conversation to respond
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Message</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Select any message from the conversation to edit it here, or type a new message manually.
                        When you click "Generate AI Response", the content in this field will be used.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={manualResponse}
                        onChange={(e) => setManualResponse(e.target.value)}
                        placeholder="Type or edit a message here..."
                        variant="outlined"
                        disabled={loading}
                        sx={{ mt: 1 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => saveDraftResponse(selectedConversation.id, manualResponse)}
                          disabled={loading || !manualResponse.trim()}
                        >
                          Save Draft
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={sendManualResponse}
                          disabled={loading || !manualResponse.trim()}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        >
                          Send Response
                        </Button>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box>
                      <Typography variant="subtitle2">AI Assistance</Typography>
                      <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        <Typography variant="body2">
                          <strong>How it works:</strong> First select a message by clicking on it (or edit/type one above), 
                          then click "Generate AI Response". The AI will analyze the message from the text field above.
                          After the response appears, you can click "View Prompt" to see what was sent to ChatGPT.
                        </Typography>
                      </Alert>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          disabled={loading || openaiStatus !== 'connected'}
                          onClick={generateAIResponse}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ChatIcon />}
                        >
                          {loading ? "Generating..." : "Generate AI Response"}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => archiveConversation(selectedConversation.id)}
                          disabled={loading}
                        >
                          Archive
                        </Button>
                      </Box>
                      
                      {aiResponse && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 2, 
                          bgcolor: 'background.default', 
                          borderRadius: 1,
                          mb: 2 
                        }}>
                          <Typography variant="subtitle2" gutterBottom>AI Generated Response:</Typography>
                          <Paper 
                            variant="outlined"
                            sx={{ 
                              height: '200px', 
                              overflowY: 'auto',
                              overflowX: 'hidden', 
                              mb: 2,
                              p: 2,
                              borderRadius: 1,
                              bgcolor: 'white',
                              boxShadow: 'inset 0 0 6px rgba(0,0,0,0.1)',
                              scrollbarWidth: 'thin',
                              '&::-webkit-scrollbar': {
                                width: '8px',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                borderRadius: '4px',
                              },
                              position: 'relative'
                            }}
                          >
                            <Typography variant="body2" sx={{ 
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              paddingBottom: '8px'
                            }}>
                              {aiResponse}
                            </Typography>
                          </Paper>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Box>
                              <Button
                                onClick={() => {
                                  setManualResponse(aiResponse);
                                  setAiResponse('');
                                }}
                                color="primary"
                                startIcon={<EditIcon />}
                                sx={{ mr: 1 }}
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => setShowPromptDialog(true)}
                                color="info"
                                variant="contained"
                                size="small"
                                disabled={!promptDetails}
                                startIcon={<PreviewIcon />}
                                sx={{ 
                                  bgcolor: 'info.main', 
                                  color: 'white',
                                  animation: promptDetails ? 'pulse 1.5s infinite' : 'none',
                                  '@keyframes pulse': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)' },
                                    '70%': { boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)' },
                                  }
                                }}
                              >
                                View Prompt
                              </Button>
                            </Box>
                            <Button
                              onClick={() => approveAndSendResponse(false)}
                              color="success"
                              variant="contained"
                              startIcon={<DoneIcon />}
                              disabled={loading}
                            >
                              Approve & Send
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      )}
      
      {/* Prompt Dialog */}
      <Dialog 
        open={showPromptDialog} 
        onClose={() => setShowPromptDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">ChatGPT Prompt Details</Typography>
            <IconButton onClick={() => setShowPromptDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {promptDetails ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f7ff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" color="primary">ChatGPT Assistant ID:</Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      startIcon={assistantIdStatus.loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={checkAssistantId}
                      disabled={assistantIdStatus.loading}
                    >
                      Check Assistant ID
                    </Button>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: assistantIdStatus.id || promptDetails?.assistantId ? '#f5f5f5' : '#fff8e1',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      border: '1px solid',
                      borderColor: assistantIdStatus.id || promptDetails?.assistantId ? 'success.light' : 'warning.light',
                      borderRadius: 1,
                      position: 'relative'
                    }}
                  >
                    {assistantIdStatus.loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography>Checking assistant ID...</Typography>
                      </Box>
                    ) : assistantIdStatus.id ? (
                      <>
                        <Chip 
                          label="Assistant ID Found" 
                          color="success" 
                          size="small" 
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        />
                        <Typography 
                          component="span" 
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', display: 'block', fontWeight: 'bold' }}
                        >
                          {assistantIdStatus.id}
                        </Typography>
                        {assistantIdStatus.message && (
                          <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                            {assistantIdStatus.message}
                          </Typography>
                        )}
                      </>
                    ) : promptDetails?.assistantId ? (
                      <>
                        <Chip 
                          label="Assistant ID Found" 
                          color="success" 
                          size="small" 
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        />
                        <Typography 
                          component="span" 
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', fontWeight: 'bold' }}
                        >
                          {promptDetails.assistantId}
                        </Typography>
                      </>
                    ) : (
                      <Box>
                        <Chip 
                          label="No Assistant ID" 
                          color="warning" 
                          size="small" 
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        />
                        <Typography color="warning.main" fontWeight="bold">
                          No assistant ID available
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                          {assistantIdStatus.error ? (
                            <>Error: {assistantIdStatus.error}</>
                          ) : (
                            <>
                              The assistant may still be initializing or hasn't been created yet.
                              Try these steps:
                              <ol style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                <li>Click "Check Assistant ID" above to try retrieving the ID again</li>
                                <li>Restart the server if the issue persists</li>
                              </ol>
                            </>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>

              {/* Base Assistant Instructions Section */}
              {promptDetails.baseAssistantInstructions && (
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      bgcolor: '#e8f5e9',
                      p: 1,
                      borderRadius: '4px 4px 0 0',
                      borderTop: '1px solid #c8e6c9',
                      borderLeft: '1px solid #c8e6c9',
                      borderRight: '1px solid #c8e6c9'
                    }}
                  >
                    <SettingsIcon sx={{ mr: 1, color: 'success.main' }} />
                    Base Assistant Instructions
                    <Tooltip title="These permanent instructions are stored in the ChatGPT assistant configuration">
                      <IconButton size="small" sx={{ ml: 1 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f9fff9',
                      maxHeight: '250px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      borderTop: 0,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0
                    }}
                  >
                    {promptDetails.baseAssistantInstructions}
                  </Paper>
                </Box>
              )}

              {/* Dynamic System Prompt Section */}
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    bgcolor: '#e3f2fd',
                    p: 1,
                    borderRadius: '4px 4px 0 0',
                    borderTop: '1px solid #bbdefb',
                    borderLeft: '1px solid #bbdefb',
                    borderRight: '1px solid #bbdefb'
                  }}
                >
                  <ChatIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Dynamic System Prompt
                  <Tooltip title="This prompt is generated dynamically for each request based on parameters, spreadsheets, and conversation context">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f5fbff',
                    maxHeight: '300px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    borderTop: 0,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0
                  }}
                >
                  {promptDetails.completeSystemPrompt || "No dynamic system prompt available"}
                </Paper>
              </Box>

              {/* Files Used in Conversation Section */}
              <Box sx={{ mb: 3 }}>
                {/* Check for files in either attachedFiles or vector stores */}
                {(!promptDetails.attachedFiles || promptDetails.attachedFiles.length === 0) && 
                 (!promptDetails.vectorStores || promptDetails.vectorStores.length === 0) ? (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 3, 
                      bgcolor: '#fff8e9',
                      borderTop: 0,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      textAlign: 'center'
                    }}
                  >
                    <Typography color="warning.main" variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      NO FILES ATTACHED TO ASSISTANT
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      In the OpenAI Assistants API, files are attached directly to the assistant and available for all conversations.
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Troubleshooting:</strong>
                        </Typography>
                        <Typography variant="body2" mt={1}>
                          <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            <li>Check that parameters have active spreadsheets in the database</li>
                            <li>Verify that all parameters are in the ASSISTANT INSTRUCTIONS category</li>
                            <li>Restart the server if files don't appear</li>
                          </ul>
                        </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" mt={1}>
                        attachedFiles property exists: {String('attachedFiles' in promptDetails)}<br />
                        attachedFiles is array: {String(Array.isArray(promptDetails.attachedFiles))}<br />
                        attachedFiles defined: {String(!!promptDetails.attachedFiles)}<br />
                        {promptDetails.attachedFiles ? `Length: ${promptDetails.attachedFiles.length}` : 'Property is null/undefined'}<br />
                        {promptDetails.fileStatistics && `File Statistics: ${JSON.stringify(promptDetails.fileStatistics)}`}
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <>
                    {/* Vector Store Information - Always show this first when available */}
                    {promptDetails.vectorStores && promptDetails.vectorStores.length > 0 && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9f5', borderTop: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" color="primary.dark" sx={{ fontWeight: 'bold', borderBottom: '2px solid #2e7d32', pb: 1 }}>
                        Vector Store Information
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={vectorStoreCleanupStatus.isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={cleanupVectorStore}
                        disabled={vectorStoreCleanupStatus.isLoading}
                      >
                        Clean Up Vector Store
                      </Button>
                    </Box>
                    
                    {vectorStoreCleanupStatus.result && (
                      <Alert severity="success" sx={{ mt: 1, mb: 2 }}>
                        Vector store cleaned up successfully. Removed {vectorStoreCleanupStatus.result.filesRemoved} duplicate files. 
                        Now contains {vectorStoreCleanupStatus.result.fileCount} unique files.
                      </Alert>
                    )}
                    {vectorStoreCleanupStatus.error && (
                      <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                        {vectorStoreCleanupStatus.error}
                      </Alert>
                    )}
                        {promptDetails.vectorStores.map((vectorStore, idx) => (
                          <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, bgcolor: '#e8f5e9', p: 1, borderRadius: 1 }}>
                              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                                {vectorStore.name}
                              </Typography>
                              <Box>
                                <Chip 
                                  label={vectorStore.status === 'available' ? 'Available' : 'Error'} 
                                  color={vectorStore.status === 'available' ? 'success' : 'error'} 
                                  size="small" 
                                  sx={{ mr: 1 }}
                                />
                                <Chip 
                                  label={`${vectorStore.file_count} Files`}
                                  color="info" 
                                  size="small" 
                                />
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold' }}>
                                <strong>Vector Store ID:</strong> {vectorStore.id}
                              </Typography>
                            </Box>
                            
                            {/* Files in this vector store */}
                            {vectorStore.files && vectorStore.files.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  Files in this vector store:
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>File ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {vectorStore.files.map((file, fileIdx) => (
                                        <TableRow key={fileIdx} hover>
                                          <TableCell>
                                          <Typography variant="body2" fontWeight="medium">
                                            {file.filename || file.original_filename || file.name || (file.id && `File-${file.id.substring(0, 8)}`) || "Unknown file"}
                                          </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                              {file.id}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={file.status || 'unknown'} 
                                              color={file.status === 'processed' ? 'success' : 'info'}
                                              size="small" 
                                              variant="outlined"
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    )}
                    
                    {/* All Files Table */}
                    <Typography variant="subtitle1" sx={{ pl: 2, pt: 2, pb: 1, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', borderBottom: '2px solid #1565c0' }}>
                      All Files Attached to Assistant
                    </Typography>
                    
                    {/* File List */}
                    {promptDetails.attachedFiles && promptDetails.attachedFiles.length > 0 ? (
                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          maxHeight: '300px',
                          overflow: 'auto',
                          borderTop: 0,
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                              <TableCell><strong>Filename</strong></TableCell>
                              <TableCell><strong>File ID</strong></TableCell>
                              <TableCell><strong>Vector Store ID</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell><strong>Parameter</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {promptDetails.attachedFiles.map((file, index) => (
                              <TableRow key={file.id || index} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <UploadIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                                  <Typography variant="body2" fontWeight="medium">
                                      {file.filename || file.original_filename || file.name || (file.id && `File-${file.id.substring(0, 8)}`) || "Unknown file"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {file.id || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {file.in_vector_store && file.vector_store_id ? (
                                    <Chip 
                                      label={file.vector_store_id}
                                      color="primary"
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      Not in vector store
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={file.status || "unknown"} 
                                    color={file.status === 'processed' ? 'success' : 'warning'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {file.parameter ? (
                                    <Box>
                                      <Typography variant="body2">
                                        <strong>{file.parameter.name}</strong>
                                        <Typography variant="caption" display="block" color="text.secondary">
                                          Category: {file.parameter.category}
                                        </Typography>
                                      </Typography>
                                      {file.parameter.context_notes && (
                                        <Button 
                                          size="small" 
                                          variant="outlined" 
                                          color="info"
                                          onClick={() => {
                                            const tooltipContent = document.createElement('div');
                                            tooltipContent.innerText = file.parameter.context_notes;
                                            const tooltip = document.createElement('div');
                                            tooltip.className = 'tooltip';
                                            tooltip.appendChild(tooltipContent);
                                            document.body.appendChild(tooltip);
                                            setTimeout(() => document.body.removeChild(tooltip), 5000);
                                          }}
                                        >
                                          View Notes
                                        </Button>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No parameter match found
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fff8e9' }}>
                        <Typography variant="body2" color="text.secondary">
                          No direct file attachments found. Files are available through vector stores shown above.
                        </Typography>
                      </Box>
                    )}
                    
                    {/* File Attachment Summary */}
                    {promptDetails.file_attachment_summary && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                          File Attachment Summary:
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Vector Store Linked:</strong> {promptDetails.file_attachment_summary.has_vector_store ? 
                                <Chip label="Yes" size="small" color="success" /> : 
                                <Chip label="No" size="small" color="error" />
                              }
                            </Typography>
                            <Typography variant="body2">
                              <strong>Vector Store Count:</strong> {promptDetails.file_attachment_summary.vector_store_count}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Files in Vector Stores:</strong> {promptDetails.file_attachment_summary.files_in_vector_stores}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Files Linked to Assistant:</strong> {promptDetails.file_attachment_summary.files_linked_to_assistant}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    
                    {/* File Statistics */}
                    {promptDetails.fileStatistics && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#f1f8e9', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>File Statistics:</strong> 
                          {promptDetails.fileStatistics.total_message_files !== undefined ? 
                            <>{promptDetails.fileStatistics.total_message_files} files attached to messages, </> : 
                            <>{promptDetails.fileStatistics.total_files || 0} total files, </>
                          }
                          {promptDetails.fileStatistics.matched_files} matched to parameters, 
                          {promptDetails.fileStatistics.unmatched_files} unmatched
                          {promptDetails.fileStatistics.database_parameters_with_files !== undefined && 
                            <>, {promptDetails.fileStatistics.database_parameters_with_files} parameters with files in database</>
                          }
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
              
              {promptDetails.userMessages && promptDetails.userMessages.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>User Messages:</Typography>
                  <List>
                    {promptDetails.userMessages.map((msg, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={`Role: ${msg.role}`}
                          secondary={
                            <Typography 
                              component="span" 
                              variant="body2" 
                              sx={{ 
                                display: 'block', 
                                whiteSpace: 'pre-wrap', 
                                fontFamily: 'monospace'
                              }}
                            >
                              {msg.content}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body1">No prompt details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Close</Button>
          {promptDetails && (
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(promptDetails.completeSystemPrompt);
                setMessage("Prompt copied to clipboard");
              }}
              color="primary"
            >
              Copy Prompt
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Parameters Tab Content */}
      {activeTab === 1 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">AI Response Parameters</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={openAddCategoryDialog}
            >
              Add Category
            </Button>
          </Box>
          
          <Typography variant="body2" paragraph>
            Define versioned parameters with effective dates that will guide AI responses to Facebook messages.
            Each parameter can include both a spreadsheet and context notes to provide comprehensive information.
          </Typography>
          
          {/* Parameters By Category */}
          {categories.length === 0 ? (
            <Alert severity="info">
              No parameter categories defined yet. Add categories manually to get started.
            </Alert>
          ) : (
            categories.map((category) => (
              <Box key={category.id} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">{category.name}</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openParameterDialog(category.id)}
                  >
                    Add Parameter
                  </Button>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {category.description}
                </Typography>
                
                {parameters.filter(p => p.categoryId === category.id).length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No parameters defined for this category yet.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Effective Date</strong></TableCell>
                          <TableCell><strong>Expiration Date</strong></TableCell>
                          <TableCell><strong>Spreadsheet</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parameters
                          .filter(p => p.categoryId === category.id)
                          .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))
                          .map((parameter) => (
                            <TableRow key={parameter.id}>
                              <TableCell>
                                <Typography variant="body2">{parameter.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {parameter.description}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={parameter.isActive ? "Active" : "Inactive"} 
                                  color={parameter.isActive ? "success" : "default"}
                                  size="small"
                                  onClick={() => toggleParameterStatus(parameter.id)}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(parameter.effectiveDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {parameter.expirationDate 
                                  ? new Date(parameter.expirationDate).toLocaleDateString() 
                                  : "No expiration"}
                              </TableCell>
                              <TableCell>
                                {parameter.hasSpreadsheet ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                      {parameter.filename}
                                    </Typography>
                                    <IconButton 
                                      size="small" 
                                      color="primary" 
                                      onClick={() => openFilePreviewDialog(parameter)}
                                    >
                                      <PreviewIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No spreadsheet
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex' }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => openParameterDialog(category.id, parameter.id)}
                                    title="Edit Parameter"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => deleteParameter(parameter.id)}
                                    title="Delete Parameter"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ))
          )}
        </Paper>
      )}
      
      {/* Add Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)}>
        <DialogTitle>Add Parameter Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategory.name}
            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            value={newCategory.description}
            onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={addCategory} variant="contained" color="primary">Add</Button>
        </DialogActions>
      </Dialog>
      
      {/* Parameter Dialog */}
      <Dialog open={parameterDialogOpen} onClose={() => setParameterDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingParameterId ? "Edit Parameter" : "Add Parameter"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Parameter Name"
                fullWidth
                required
                variant="outlined"
                value={parameterData.name}
                onChange={(e) => setParameterData({...parameterData, name: e.target.value})}
                helperText="Name for this parameter version (e.g., 'Summer 2025 Pricing')"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Description (Optional)"
                fullWidth
                variant="outlined"
                value={parameterData.description}
                onChange={(e) => setParameterData({...parameterData, description: e.target.value})}
                helperText="Brief description of this parameter version"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                margin="dense"
                label="Effective Date"
                required
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={parameterData.effectiveDate}
                onChange={(e) => setParameterData({...parameterData, effectiveDate: e.target.value})}
                helperText="Date when this parameter version becomes active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                margin="dense"
                label="Expiration Date (Optional)"
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={parameterData.expirationDate}
                onChange={(e) => setParameterData({...parameterData, expirationDate: e.target.value})}
                helperText="Leave blank if no expiration"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={parameterData.isActive}
                      onChange={(e) => setParameterData({...parameterData, isActive: e.target.checked})}
                    />
                  }
                  label="Active"
                />
                <FormHelperText>Inactive parameters won't be used for AI responses</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Upload Spreadsheet (Optional)
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 1 }}
                >
                  Select File
                  <VisuallyHiddenInput 
                    type="file" 
                    onChange={handleParameterFileSelect} 
                    accept=".xlsx,.xls,.csv,.pdf"
                    ref={fileInputRef}
                  />
                </Button>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {parameterData.file ? `Selected: ${parameterData.file.name}` : (
                    editingParameterId && parameters.find(p => p.id === editingParameterId)?.hasSpreadsheet 
                      ? `Current file: ${parameters.find(p => p.id === editingParameterId)?.filename}` 
                      : 'No file selected'
                  )}
                </Typography>
                <FormHelperText>
                  Only Excel (.xlsx, .xls) , PDF (.pdf) and CSV (.csv) files are accepted
                </FormHelperText>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Context Notes"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={parameterData.contextNotes}
                onChange={(e) => setParameterData({...parameterData, contextNotes: e.target.value})}
                helperText="Additional context to help the AI interpret this parameter and spreadsheet (if any)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParameterDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={saveParameter} 
            variant="contained" 
            color="primary"
            disabled={!parameterData.name || !parameterData.effectiveDate}
          >
            {editingParameterId ? "Save Changes" : "Add Parameter"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* File Preview Dialog */}
      <Dialog 
        open={filePreviewDialogOpen} 
        onClose={() => setFilePreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Spreadsheet Preview: {selectedFile?.filename}
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Parameter: {selectedFile.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Effective: {new Date(selectedFile.effectiveDate).toLocaleDateString()}
                  {selectedFile.expirationDate ? ` to ${new Date(selectedFile.expirationDate).toLocaleDateString()}` : ' (no expiration)'}
                </Typography>
              </Box>
              
              {selectedFile.contextNotes && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>Context Notes:</Typography>
                  <Typography variant="body2">{selectedFile.contextNotes}</Typography>
                </Paper>
              )}
              
              {filePreview && Object.entries(filePreview).map(([sheetName, rows]) => (
                <Box key={sheetName} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Sheet: {sheetName}
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {Object.keys(rows[0] || {}).map((header, i) => (
                            <TableCell key={i}><strong>{header}</strong></TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {Object.values(row).map((cell, cellIndex) => (
                              <TableCell key={cellIndex}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilePreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FacebookMessageManager;